import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, 
  Mail, 
  Calendar, 
  MessageSquare, 
  Trash2, 
  Edit3, 
  Save,
  Eye,
  EyeOff,
  LogOut,
  Building,
  Phone,
  MapPin,
  Shield,
  Settings,
  X,
  Download,
  AlertTriangle
} from 'lucide-react';
import { 
  signOutUser, 
  updateUserProfile, 
  getUserChatHistory, 
  deleteUserChat, 
  clearUserChatHistory,
  getCurrentUserData,
  updateUserDocument,
  ensureUserDocumentExists
} from '../../services/authService';
import { getSystemSettings } from '../../services/firebase';
import toast from 'react-hot-toast';

const UserArea = ({ isOpen, onClose, user, darkMode }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [systemSettings, setSystemSettings] = useState(null);
  const [userData, setUserData] = useState(null);
  const [profileData, setProfileData] = useState({
    displayName: '',
    email: '',
    company: '',
    phone: '',
    location: '',
    designation: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (user && isOpen) {
      loadUserData();
      loadChatHistory();
      loadSystemSettings();
    }
  }, [user, isOpen]);

  const loadUserData = async () => {
    try {
      const currentUserData = await getCurrentUserData();
      if (currentUserData) {
        setUserData(currentUserData);
        setProfileData({
          displayName: currentUserData.displayName || '',
          email: currentUserData.email || '',
          company: currentUserData.company || '',
          phone: currentUserData.phone || '',
          location: currentUserData.location || '',
          designation: currentUserData.designation || '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadSystemSettings = async () => {
    try {
      const settings = await getSystemSettings();
      setSystemSettings(settings);
    } catch (error) {
      console.error('Error loading system settings:', error);
    }
  };

  const loadChatHistory = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const history = await getUserChatHistory(user.uid);
      setChatHistory(history);
    } catch (error) {
      console.error('Error loading chat history:', error);
      toast.error('Failed to load chat history');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      setLoading(true);
      console.log('🔄 Starting profile update for user:', user.uid);

      // Validate passwords if provided
      if (profileData.newPassword) {
        if (profileData.newPassword !== profileData.confirmPassword) {
          toast.error('Passwords do not match');
          return;
        }
        if (profileData.newPassword.length < 6) {
          toast.error('Password must be at least 6 characters');
          return;
        }
      }

      // Update additional user data in Firestore first
      console.log('📝 Updating user document with data:', {
        displayName: profileData.displayName,
        company: profileData.company,
        phone: profileData.phone,
        location: profileData.location,
        designation: profileData.designation
      });
      
      const updateResult = await updateUserDocument(user.uid, {
        displayName: profileData.displayName,
        company: profileData.company,
        phone: profileData.phone,
        location: profileData.location,
        designation: profileData.designation
      });

      console.log('✅ Firestore update result:', updateResult);

      if (!updateResult.success) {
        console.error('❌ Firestore update failed:', updateResult.error);
        toast.error('Failed to update user data: ' + updateResult.error);
        return;
      }

      // Update profile (display name and password)
      const result = await updateUserProfile(
        profileData.displayName,
        profileData.newPassword || null
      );

      console.log('✅ Profile update result:', result);

      if (result.success) {
        setEditMode(false);
        setProfileData(prev => ({ ...prev, newPassword: '', confirmPassword: '' }));
        await loadUserData(); // Reload user data
        toast.success('Profile updated successfully');
      } else {
        console.error('❌ Profile update failed:', result.error);
        toast.error('Failed to update profile: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Error updating profile:', error);
      toast.error('Failed to update profile: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteChat = async (chatId) => {
    if (!systemSettings?.userPermissions?.allowChatDeletion) {
      toast.error('Chat deletion is not allowed by administrator');
      return;
    }

    if (window.confirm('Are you sure you want to delete this chat?')) {
      try {
        const result = await deleteUserChat(chatId, user.uid);
        if (result.success) {
          setChatHistory(prev => prev.filter(chat => chat.id !== chatId));
        }
      } catch (error) {
        console.error('Error deleting chat:', error);
      }
    }
  };

  const handleClearAllChats = async () => {
    if (!systemSettings?.userPermissions?.allowChatDeletion) {
      toast.error('Chat deletion is not allowed by administrator');
      return;
    }

    if (window.confirm('Are you sure you want to delete ALL your chat history? This action cannot be undone.')) {
      try {
        setLoading(true);
        const result = await clearUserChatHistory(user.uid);
        if (result.success) {
          setChatHistory([]);
        }
      } catch (error) {
        console.error('Error clearing chat history:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSyncProfile = async () => {
    try {
      setLoading(true);
      console.log('🔄 Manual profile sync initiated...');
      
      const syncResult = await ensureUserDocumentExists();
      console.log('📝 Manual sync result:', syncResult);
      
      if (syncResult.success) {
        await loadUserData();
        toast.success('Profile synced successfully! You should now appear in the admin panel.');
      } else {
        toast.error('Failed to sync profile: ' + syncResult.error);
      }
    } catch (error) {
      console.error('❌ Error syncing profile:', error);
      toast.error('Failed to sync profile: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOutUser();
      onClose();
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out');
    }
  };

  const exportChatHistory = () => {
    if (chatHistory.length === 0) {
      toast.error('No chat history to export');
      return;
    }

    const csvContent = [
      ['Date', 'Your Message', 'AI Response', 'Language'],
      ...chatHistory.map(chat => [
        chat.timestamp?.toDate?.()?.toLocaleString() || 'N/A',
        chat.userMessage || '',
        chat.aiResponse || '',
        chat.language || 'en'
      ])
    ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat_history_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Chat history exported successfully');
  };

  if (!isOpen || !user) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className={`w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl ${
            darkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white text-lg font-bold">
                  {userData?.displayName ? userData.displayName.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 className="text-xl font-semibold">
                    {userData?.displayName || 'User Profile'}
                  </h2>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {user.email}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className={`p-2 rounded-lg transition-colors ${
                  darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex space-x-1 mt-6">
              {[
                { id: 'profile', label: 'Profile', icon: User },
                { id: 'chats', label: 'Chat History', icon: MessageSquare },
                { id: 'settings', label: 'Settings', icon: Settings }
              ].map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-600 text-white'
                        : darkMode
                        ? 'text-gray-400 hover:text-white hover:bg-gray-800'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Profile Information</h3>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleSyncProfile}
                      disabled={loading}
                      className="flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors bg-yellow-600 hover:bg-yellow-700 text-white disabled:opacity-50"
                      title="Sync your profile to ensure it appears in the admin panel"
                    >
                      <AlertTriangle className="w-4 h-4" />
                      <span>Sync Profile</span>
                    </button>
                    {systemSettings?.userPermissions?.allowProfileEdit && (
                      <button
                        onClick={() => setEditMode(!editMode)}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                          editMode
                            ? 'bg-gray-600 hover:bg-gray-700 text-white'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                      >
                        {editMode ? <X className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
                        <span>{editMode ? 'Cancel' : 'Edit Profile'}</span>
                      </button>
                    )}
                  </div>
                </div>

                {editMode ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Display Name</label>
                        <input
                          type="text"
                          value={profileData.displayName}
                          onChange={(e) => setProfileData(prev => ({
                            ...prev,
                            displayName: e.target.value
                          }))}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                          }`}
                          placeholder="Enter your display name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Company</label>
                        <input
                          type="text"
                          value={profileData.company}
                          onChange={(e) => setProfileData(prev => ({
                            ...prev,
                            company: e.target.value
                          }))}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                          }`}
                          placeholder="Enter your company"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Phone</label>
                        <input
                          type="tel"
                          value={profileData.phone}
                          onChange={(e) => setProfileData(prev => ({
                            ...prev,
                            phone: e.target.value
                          }))}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                          }`}
                          placeholder="Enter your phone number"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Location</label>
                        <input
                          type="text"
                          value={profileData.location}
                          onChange={(e) => setProfileData(prev => ({
                            ...prev,
                            location: e.target.value
                          }))}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                          }`}
                          placeholder="Enter your location"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Designation</label>
                        <select
                          value={profileData.designation}
                          onChange={(e) => setProfileData(prev => ({
                            ...prev,
                            designation: e.target.value
                          }))}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                          }`}
                        >
                          <option value="">Select your designation</option>
                          <option value="intern">Intern</option>
                          <option value="junior">Junior</option>
                          <option value="mid-level">Mid-Level</option>
                          <option value="senior">Senior</option>
                          <option value="lead">Lead</option>
                          <option value="manager">Manager</option>
                          <option value="director">Director</option>
                          <option value="vp">Vice President</option>
                          <option value="ceo">CEO</option>
                          <option value="cto">CTO</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    {systemSettings?.userPermissions?.allowPasswordChange && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">New Password (optional)</label>
                          <div className="relative">
                            <input
                              type={showPassword ? 'text' : 'password'}
                              value={profileData.newPassword}
                              onChange={(e) => setProfileData(prev => ({
                                ...prev,
                                newPassword: e.target.value
                              }))}
                              className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                              }`}
                              placeholder="Enter new password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            >
                              {showPassword ? (
                                <EyeOff className="w-4 h-4 text-gray-400" />
                              ) : (
                                <Eye className="w-4 h-4 text-gray-400" />
                              )}
                            </button>
                          </div>
                        </div>

                        {profileData.newPassword && (
                          <div>
                            <label className="block text-sm font-medium mb-2">Confirm New Password</label>
                            <input
                              type="password"
                              value={profileData.confirmPassword}
                              onChange={(e) => setProfileData(prev => ({
                                ...prev,
                                confirmPassword: e.target.value
                              }))}
                              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                              }`}
                              placeholder="Confirm new password"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    <button
                      onClick={handleUpdateProfile}
                      disabled={loading}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                    >
                      <Save className="w-4 h-4" />
                      <span>{loading ? 'Updating...' : 'Update Profile'}</span>
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="flex items-center space-x-3">
                          <User className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500">Display Name</p>
                            <p className="font-medium">{userData?.displayName || 'Not set'}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Mail className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500">Email</p>
                            <p className="font-medium">{user.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Building className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500">Company</p>
                            <p className="font-medium">{userData?.company || 'Not set'}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Shield className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500">Designation</p>
                            <p className="font-medium">{userData?.designation || 'Not set'}</p>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div className="flex items-center space-x-3">
                          <Phone className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500">Phone</p>
                            <p className="font-medium">{userData?.phone || 'Not set'}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <MapPin className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500">Location</p>
                            <p className="font-medium">{userData?.location || 'Not set'}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Calendar className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500">Member Since</p>
                            <p className="font-medium">
                              {userData?.createdAt?.toDate?.()?.toLocaleDateString() || 
                               user.metadata?.creationTime ? 
                               new Date(user.metadata.creationTime).toLocaleDateString() : 
                               'Unknown'
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Chat History Tab */}
            {activeTab === 'chats' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Chat History ({chatHistory.length})</h3>
                  <div className="flex space-x-2">
                    {chatHistory.length > 0 && (
                      <button
                        onClick={exportChatHistory}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        <span>Export</span>
                      </button>
                    )}
                    {systemSettings?.userPermissions?.allowChatDeletion && chatHistory.length > 0 && (
                      <button
                        onClick={handleClearAllChats}
                        disabled={loading}
                        className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Clear All</span>
                      </button>
                    )}
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-500">Loading chat history...</p>
                  </div>
                ) : chatHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500">No chat history yet</p>
                    <p className="text-sm text-gray-400">Start a conversation to see your chat history here</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {chatHistory.map((chat) => (
                      <div
                        key={chat.id}
                        className={`p-4 rounded-lg border ${
                          darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-xs text-gray-500">
                            {chat.timestamp?.toDate?.()?.toLocaleString() || 'Unknown time'}
                          </span>
                          {systemSettings?.userPermissions?.allowChatDeletion && (
                            <button
                              onClick={() => handleDeleteChat(chat.id)}
                              className="text-red-500 hover:text-red-700 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <div className="space-y-2">
                          <div>
                            <p className="text-sm font-medium text-blue-600">You:</p>
                            <p className="text-sm">{chat.userMessage}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-green-600">AI:</p>
                            <p className="text-sm">{chat.aiResponse}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold">Account Settings</h3>
                
                <div className="space-y-4">
                  <div className={`p-4 rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center space-x-3">
                      <Shield className="w-5 h-5 text-blue-600" />
                      <div>
                        <h4 className="font-medium">Account Security</h4>
                        <p className="text-sm text-gray-500">
                          Email verification: {user.emailVerified ? 
                            <span className="text-green-600">Verified</span> : 
                            <span className="text-yellow-600">Pending</span>
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className={`p-4 rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center space-x-3">
                      <MessageSquare className="w-5 h-5 text-green-600" />
                      <div>
                        <h4 className="font-medium">Chat Permissions</h4>
                        <p className="text-sm text-gray-500">
                          Chat deletion: {systemSettings?.userPermissions?.allowChatDeletion ? 
                            <span className="text-green-600">Enabled</span> : 
                            <span className="text-red-600">Disabled by admin</span>
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  {!systemSettings?.userPermissions?.allowChatDeletion && (
                    <div className={`p-4 rounded-lg border border-yellow-300 bg-yellow-50 ${darkMode ? 'bg-yellow-900/20 border-yellow-600' : ''}`}>
                      <div className="flex items-start space-x-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-yellow-800 dark:text-yellow-200">Chat Deletion Disabled</h4>
                          <p className="text-sm text-yellow-700 dark:text-yellow-300">
                            The administrator has disabled chat deletion for users. Contact support if you need to delete your chat history.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={handleSignOut}
                      className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UserArea; 